import { z } from 'zod';
/**
 * Shared building blocks for the intake form definition (ADR-017, FR-INTAKE-01).
 *
 * A form definition is authored by clinic staff in the builder (M8.5), stored as JSON in
 * `intake_form_versions.schema` and rendered by `@platform/ui`'s FormRenderer (M8.4). It is
 * therefore *untrusted input that later becomes UI*, so every string here is bounded and
 * markup-free, and every regular expression a definition may carry comes from a fixed
 * allow-list rather than from the author (rules §23, §28.1).
 */
/** Hard ceilings. A definition outside these is rejected, not truncated. */
export const FORM_LIMITS = {
    /** Fields across all sections. The HeyAllergy Jotform is ~120 (FR-INTAKE-02). */
    maxFields: 300,
    maxSections: 60,
    maxFieldsPerSection: 120,
    maxOptions: 200,
    /** Nesting of `all` / `any` groups inside one condition. */
    maxConditionDepth: 3,
    maxConditionChildren: 20,
    maxLabel: 300,
    maxParagraph: 5000,
    maxHelpText: 500,
    maxPlaceholder: 120,
    /** Answer ceilings, applied by `validateAnswers`. */
    maxTextAnswer: 1000,
    maxTextareaAnswer: 10000,
    maxFilesPerField: 10,
    maxFileSizeMb: 25,
};
/**
 * Anything that could open a tag. The renderer never sets `innerHTML`, so this is
 * defence in depth for the other consumers of the JSON (PDF export M8.8, emails).
 */
const HTML_LIKE = /<\s*[A-Za-z/!?]/;
/** A bounded, trimmed, markup-free string. Every author-supplied string uses this. */
export function plainText(max) {
    return z
        .string()
        .trim()
        .min(1, { error: 'Required.' })
        .max(max, { error: `Must be ${max} characters or fewer.` })
        .refine((value) => !HTML_LIKE.test(value), { error: 'HTML is not allowed in form text.' });
}
/** Identifiers the builder generates: stable, URL-safe, usable as a DOM id suffix. */
export const FieldIdSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/, {
    error: 'Ids start with a letter: letters, digits, _ and - only.',
});
export const SectionIdSchema = FieldIdSchema;
/** The definition's own id and the form key from `intake_forms.key`. */
export const FormIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_.:-]+$/);
export const FormKeySchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/, {
    error: 'Use a lower-case slug, e.g. "new-patient-intake".',
});
/** `en`, `en-US`, `es-MX`. */
export const LocaleSchema = z
    .string()
    .regex(/^[a-z]{2}(-[A-Z]{2})?$/, { error: 'Use a BCP-47 tag such as "en-US".' });
/**
 * The only regular expressions a field may reference.
 *
 * Author-supplied patterns are not accepted at all: a builder user cannot be expected to
 * reason about catastrophic backtracking, and the pattern is evaluated both in the browser
 * and in the API. Every source below is linear - a single character class with one
 * quantifier, no nesting, no alternation inside a quantifier - so it cannot backtrack.
 * Adding a key here is a code change and a review.
 */
export const FIELD_PATTERNS = {
    digits: { source: '^[0-9]+$', message: 'Use digits only.' },
    alphanumeric: { source: '^[A-Za-z0-9]+$', message: 'Use letters and digits only.' },
    name: {
        source: "^[A-Za-z .'-]{1,80}$",
        message: 'Use letters, spaces, apostrophes and hyphens.',
    },
    zipUs: { source: '^[0-9]{5}$', message: 'Enter a 5-digit ZIP code.' },
    phoneUs: { source: '^[0-9]{10}$', message: 'Enter a 10-digit phone number.' },
    memberId: {
        source: '^[A-Za-z0-9-]{4,32}$',
        message: 'Enter the id exactly as it appears on the card.',
    },
    last4: { source: '^[0-9]{4}$', message: 'Enter the last 4 digits.' },
    dateIso: { source: '^[0-9]{4}-[0-9]{2}-[0-9]{2}$', message: 'Use the date picker.' },
};
export const FIELD_PATTERN_KEYS = Object.keys(FIELD_PATTERNS);
/** Compiles a pattern key. Sources are literals in this file, never author input. */
export function fieldPattern(key) {
    return new RegExp(FIELD_PATTERNS[key].source);
}
/** `YYYY-MM-DD`, the wire format for every `date` answer. */
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
//# sourceMappingURL=primitives.js.map